import { createSelector } from '@ngrx/store';
import {
  selectAllAiLists,
  selectSearchResults,
  selectFilterOptions,
  selectListSearchKey,
  selectSelectedAiList,
  selectMenuSearchSortFilterOptions,
  IAIListFilterState,
} from '@usf/ngrx-list-ai';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { selectOnline } from '@panamax/app-state';
import { AIList, AIListItem, JobStatus } from '@usf/list-ai-types';
import {
  AIListBuilderViewModel,
  FileParse,
  ProductMatch,
} from '../model/ai-list-builder-view.model';
import { AIListProductCardViewModel } from '../model/ai-list-product-card.model';
import { ListsViewModel } from '../model/lists-view.model';
import { ListKey } from '@usf/list-types';
import {
  appStateForProductsSelector,
  productWithAlternativesSelector,
} from '@shared/selectors/product.selectors';
import { Product } from '@shared/models/product.model';
import { isProductRestricted } from '@shared/selectors/helpers/product-info.selectors.helper';
import { AppStateForProduct } from '@shared/models/app-state-for-product';
import { productMatchesKeyword } from '../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';

export const alteranitiveIngredientsSelector = createSelector(
  selectSearchResults,
  selectMenuSearchSortFilterOptions,
  productWithAlternativesSelector,
  appStateForProductsSelector,
  (
    searchResultsState,
    menuOptions,
    productMap: Map<number, Product>,
    appStateForProduct: AppStateForProduct,
  ) => {
    if (!searchResultsState.searchResults) {
      return [];
    }
    const alternativeProducts: Product[] =
      searchResultsState.searchResults.products
        .map(result => {
          const product = productMap.get(Number(result.productNumber));
          return product;
        })
        .filter(
          product =>
            product &&
            !isProductRestricted(
              product,
              appStateForProduct,
              product.isMslRestricted,
            ),
        );

    // First, apply filtering on the raw search results data
    // This is more efficient than mapping to products first
    const filteredSearchResults: Product[] = filterSearchResults(
      alternativeProducts,
      menuOptions?.filters || [],
      menuOptions?.searchKey || '',
    );

    // Then map the filtered results to actual product objects
    return filteredSearchResults;
  },
);

/**
 * Selector that returns alternative ingredients with the currently selected product moved to the beginning
 */
export const alternativeIngredientsWithCurrentFirstSelector = createSelector(
  alteranitiveIngredientsSelector,
  selectSelectedAiList,
  (ingredients: Product[], aiList: AIList) => {
    if (!ingredients || ingredients.length === 0 || !aiList) {
      return ingredients;
    }

    // Try to find the current matching product from the AI List data
    // AIList contains groups with groupItems, each having a match with productNumber
    let currentProductNumber: string | number | undefined;

    // Find the currently active product from any available item in the list
    for (const group of aiList.groups || []) {
      for (const item of group.groupItems || []) {
        if (item?.match?.productNumber) {
          currentProductNumber = item.match.productNumber;
          break;
        }
      }
      if (currentProductNumber) break;
    }

    if (!currentProductNumber) {
      return ingredients;
    }

    // Find the current product in the alternatives array by comparing strings
    const currentProductIndex = ingredients.findIndex(
      product => String(product.productNumber) === String(currentProductNumber),
    );

    // If current product is in the alternatives, move it to front
    if (currentProductIndex > 0) {
      const result = [...ingredients];
      const currentProduct = result.splice(currentProductIndex, 1)[0];
      return [currentProduct, ...result];
    }

    return ingredients;
  },
);

/**
 * Selector that returns alternative ingredients and highlights if any match the current ingredient
 * This avoids having to subscribe to multiple streams in components
 */
export const alternativeIngredientsWithSelectionInfoSelector = createSelector(
  alternativeIngredientsWithCurrentFirstSelector,
  selectSelectedAiList,
  (ingredients: Product[], aiList: AIList) => {
    if (!ingredients || ingredients.length === 0 || !aiList) {
      return {
        ingredients,
        matchingProductNumber: null,
      };
    }

    // We won't try to determine the current product from the aiList anymore
    // Instead, the service will keep track of the current product number being edited

    console.log('Selector returning:', {
      ingredientCount: ingredients.length,
      firstIngredient:
        ingredients.length > 0 ? ingredients[0].productNumber : null,
    });

    return {
      ingredients,
      // The service will handle finding the matching product
      matchingProductNumber: null,
    };
  },
);

/**
 * Filters the raw search results based on menu filter options and search key
 * Performs filtering on the pre filtered Products before returning further filtered Products
 *
 * @param alternativeProducts Array of raw search result items
 * @param filters Array of filter IDs that are active
 * @param searchKey Search text to filter by
 * @returns Filtered array of Products
 */
export const filterSearchResults = (
  alternativeProducts: Product[],
  filters: string[],
  searchKey: string,
): Product[] => {
  // If no filters and no search key, return all results
  if ((!filters || filters.length === 0) && (!searchKey || searchKey === '')) {
    return alternativeProducts;
  }

  // Apply filters to the search results
  return alternativeProducts.filter(alternativeProduct => {
    // Apply filter criteria if any filters are active
    if (filters && filters.length > 0) {
      // Map the product properties to our filter options
      const filterConditions = {
        'direct': alternativeProduct.inventory?.isDirect === true,
        'early-cutoff': alternativeProduct.inventory?.justInTime === true,
        'discontinued': alternativeProduct.inventory?.productStatus === '8',
        'exclusive': alternativeProduct.summary?.isExclusiveBrand === true,
      };

      // Check if any active filter matches the product
      const passesFilter = filters.some(
        filter => filterConditions[filter as keyof typeof filterConditions],
      );

      // If no filter matches, exclude the product
      if (!passesFilter) {
        return false;
      }
    }

    // Then apply search criteria if a search key is present
    if (searchKey && searchKey !== '') {
      // Use the same productMatchesKeyword function for consistent search behavior
      return productMatchesKeyword(
        searchKey,
        alternativeProduct.summary,
        undefined,
        alternativeProduct.customerProductNumber,
      );
    }

    // If it passed all active filters and search criteria, include it
    return true;
  });
};

export const selectAIListBuilderViewModel = createSelector(
  selectSelectedAiList,
  selectOnline(),
  productWithAlternativesSelector,
  selectFilterOptions,
  selectListSearchKey,
  alteranitiveIngredientsSelector,
  (
    aiList: AIList,
    isOnline: boolean,
    productMap: Map<number, Product>,
    filterState: IAIListFilterState,
    searchKey: string,
    alternativeIngredients: Product[],
  ): AIListBuilderViewModel => {
    const items = getAIListItemsFromList(aiList, productMap);
    // Item heights for all platforms and best selling products flow is 221.2, this will soon change to a couple different heights
    // that will have more logic involved once we start showing product data and what file the product was found in.

    const showSearchAndFilter = items.some(item => !item.loading);

    const filteredItems: AIListProductCardViewModel[] = filterItems(
      items,
      filterState,
      searchKey,
    );

    const itemHeights: number[] = filteredItems.map(
      (item: AIListProductCardViewModel) => setHeight(item),
    );

    const filterOptions = Object.entries(filterState).map(([key, value]) => {
      let label = '';
      switch (key) {
        case 'direct':
          label = 'Direct';
          break;
        case 'earlyCutoff':
          label = 'Early Cutoff';
          break;
        case 'discontinuedWhenOut':
          label = 'Discontinued When Out';
          break;
        case 'exclusiveBrands':
          label = 'Exclusive Brands';
          break;
      }
      return {
        id: key,
        label,
        selected: value,
      };
    });

    let selectedFilterCount = 0;
    for (const option of filterOptions) {
      if (option.selected) {
        selectedFilterCount++;
      }
    }

    return {
      aiListId: aiList?.aiListId,
      aiListName: aiList?.aiListName,
      alternativeIngredients,
      breadcrumbs: getAiListBuilderBreadCrumbs(),
      ingredientPreference: aiList?.menuProductType,
      numberOfFilesUploaded: aiList?.sourceFiles?.length ?? 0,
      menuType: aiList?.menuType,
      numberOfProducts: items?.length,
      items: filteredItems,
      matchedProductsCount:
        items?.filter(item => item.product?.summary)?.length || 0,
      isOnline,
      itemHeights,
      fileParse: selectFileParseForSelectedAiList(aiList),
      productMatch: selectProductMatchForSelectedAiList(aiList),
      updatedByUser: aiList?.updatedByUser,
      filterOptions,
      selectedFilterCount,
      showSearchAndFilter,
    } as AIListBuilderViewModel;
  },
);

export const selectAiListsAsListViewModels = createSelector(
  selectAllAiLists,
  selectOnline(),
  (aiLists: AIList[]) => {
    const aiListViewModels: ListsViewModel[] = [];
    aiLists.forEach(aiList => {
      const items = getAIListItemsFromList(aiList);
      aiListViewModels.push({
        listKey: {
          listId: undefined,
          listTypeId: 'AI',
        } as ListKey,
        r4TempListId: undefined,
        listName: aiList.aiListName,
        divisionNumber: aiList.divisionNumber,
        customerNumber: aiList.customerNumber,
        departmentNumber: 0,
        listState: aiList.listState,
        effDate: '',
        endDate: '',
        listItemCount: items.length,
        discontinuedCount: 0,
        listGroupKeys: undefined,
        deleteDtm: '',
        updatedByUser: aiList.updatedByUser,
        isEnrichment: false,
        addUserIdSourceSystem: aiList.createdByUser?.userName,
        addDtmSourceSystem: aiList.addDtm,
        addSourceSystem: aiList.sourceSystem,
        updateUserIdSourceSystem: aiList.updatedByUser?.userName,
        updateDtmSourceSystem: aiList.updateDtm,
        updateSourceSystem: aiList.sourceSystem,
        version: '',
        aiListId: aiList.aiListId,
        showEllipsis: false,
      } as ListsViewModel);
    });
    return aiListViewModels;
  },
);

// Logic for this may change after meeting on 3/13/25
export const selectFileParseForSelectedAiList = (aiList: AIList): FileParse => {
  return {
    isParsingFiles:
      aiList?.parsed === false &&
      aiList?.parseJobStates?.length > 0 &&
      aiList?.sourceFiles?.length > 0,
    fileBeingParsedDisplayText: getFileBeingParsedDisplayText(aiList),
  } as FileParse;
};

export const selectProductMatchForSelectedAiList = (
  aiList: AIList,
): ProductMatch => {
  // Count the number of items that have a match
  const matchedItems =
    aiList?.groups?.reduce((acc, group) => {
      return (
        acc +
        (group?.groupItems?.filter(item => item?.match?.productNumber)
          ?.length || 0)
      );
    }, 0) || 0;

  return {
    isMatchingProducts:
      aiList?.matched === false &&
      aiList?.matchJobState.jobStatus === JobStatus.PROCESSING,
    numberOfProductsMatched: matchedItems,
  };
};

const getFileBeingParsedDisplayText = (aiList: AIList): string => {
  let stringToReturn = '';
  // Count the number of completed parse jobs
  const numberOfFilesParsed =
    aiList?.parseJobStates?.filter(job => job.jobStatus === 'COMPLETED')
      ?.length || 0;
  const totalFiles = aiList?.sourceFiles?.length || 0;

  // If we haven't parsed all files yet, show the next ordinal number
  if (numberOfFilesParsed < totalFiles) {
    return numberToDisplayTextMap.get(numberOfFilesParsed + 1);
  }
  return stringToReturn;
};

const numberToDisplayTextMap = new Map([
  [1, 'first'],
  [2, 'second'],
  [3, 'third'],
  [4, 'fourth'],
  [5, 'fifth'],
  [6, 'sixth'],
  [7, 'seventh'],
  [8, 'eighth'],
  [9, 'ninth'],
]);

const getAiListBuilderBreadCrumbs = (): Crumb[] => {
  return [
    {
      label: 'i18n.breadcrumbs.home',
      url: '/desktop/home',
      active: false,
    },
    {
      label: 'i18n.breadcrumbs.myLists',
      url: '/desktop/lists',
      active: false,
    },
    {
      label: 'AI List Builder',
      url: '',
      active: true,
    },
  ];
};

const getAIListItemsFromList = (
  aiList: AIList,
  productMap?: Map<number, Product>,
): AIListProductCardViewModel[] => {
  let items: AIListProductCardViewModel[] = [];
  let lineNumber = 1;

  const isParsingCompleted = aiList?.parsed ?? false;

  if (aiList?.groups?.length > 0) {
    for (const group of aiList.groups) {
      if (group?.groupItems?.length > 0) {
        for (const item of group.groupItems) {
          const itemProductCardViewModel = createAIListProductCardViewModel(
            item,
            lineNumber,
            productMap,
            isParsingCompleted,
          );
          items.push(itemProductCardViewModel);
          lineNumber++;
        }
      }
    }
  }
  return items;
};

const createAIListProductCardViewModel = (
  item: AIListItem,
  index: number,
  productMap: Map<number, Product>,
  isParsingCompleted: boolean,
): AIListProductCardViewModel => {
  const product = productMap?.get(Number(item?.match?.productNumber));
  const sourceFileName = removeDirectoryFromPath(item?.sourceFileName);
  const loaded =
    !!product?.summary?.brand || !!product?.notFound || isParsingCompleted;

  return {
    product: productMap?.get(Number(item?.match?.productNumber)),
    aiListItem: item,
    sourceFileDisplayText:
      item?.sourceFileName === 'Best Selling PIM Groups'
        ? 'i18n.lists.bestSellingProducts'
        : sourceFileName,
    loading: !loaded,
    lineNumber: index,
    isMatchingFailed: (isParsingCompleted && !product) ?? false,
  } as AIListProductCardViewModel;
};

export function removeDirectoryFromPath(path: string): string {
  return path.split('/').pop() || path;
}

export const setHeight = (item: AIListProductCardViewModel): number => {
  if (item.loading && !item.product) {
    // Regular loading
    return 221.2;
  } else {
    let offset = 0;
    // If there is a drawer
    if (item?.product?.drawerMsg?.isWarning) {
      offset += 40;
    }
    // if summary category doesnt exist
    if (
      !item?.product?.summary ||
      !item?.product?.summary.categoryDescription
    ) {
      offset -= 0.8;
    }
    return 221.2 + offset;
  }
};

export const filterItems = (
  items: AIListProductCardViewModel[],
  filterState: IAIListFilterState,
  searchKey: string,
): AIListProductCardViewModel[] => {
  return items.filter(item => {
    if (!item?.product?.summary) return false;
    return (
      isInFilter(item, filterState) &&
      enhancedProductMatchesKeyword(searchKey, item)
    );
  });
};

/**
 * Checks if a product matches the active filters by using filterSearchResults.
 * This function converts the filter state to the format expected by filterSearchResults
 * and uses it to check if the product matches any of the active filters.
 *
 * @param item - The AIListProductCardViewModel containing the product to check
 * @param filterState - The current state of filters (earlyCutoff, discontinuedWhenOut, direct, exclusiveBrands)
 * @returns true if no filters are active or if the product matches any active filter
 */
export const isInFilter = (
  item: AIListProductCardViewModel,
  filterState: IAIListFilterState,
) => {
  // If no filters are active, return true
  if (!Object.values(filterState).some(value => value)) {
    return true;
  }

  // Convert filterState to array of active filters
  const activeFilters = Object.entries(filterState)
    .filter(([_, isActive]) => isActive)
    .map(([key]) => {
      // Map the filter state keys to the filter names used in filterSearchResults
      switch (key) {
        case 'earlyCutoff':
          return 'early-cutoff';
        case 'discontinuedWhenOut':
          return 'discontinued';
        case 'direct':
          return 'direct';
        case 'exclusiveBrands':
          return 'exclusive';
        default:
          return key;
      }
    });

  // Use filterSearchResults to check if the product matches the filters
  const matchesFilters =
    filterSearchResults([item.product], activeFilters, '').length > 0;

  return matchesFilters;
};

/**
 * Enhanced keyword matching function that combines the search capabilities
 * of productMatchesKeyword with additional card-specific search fields.
 */
export const enhancedProductMatchesKeyword = (
  keyword: string,
  item: AIListProductCardViewModel,
): boolean => {
  if (!keyword || keyword === '') return true;

  // First try productMatchesKeyword
  const matchesProduct = productMatchesKeyword(
    keyword,
    item.product?.summary,
    undefined,
    item.product?.customerProductNumber,
  );

  if (matchesProduct) return true;

  // If no match found in main product, check card-specific fields
  const searchKey = keyword.toLowerCase();

  // Search in category description (title)
  const categoryDescription =
    item?.product?.summary?.categoryDescription?.toLowerCase();
  if (categoryDescription?.includes(searchKey)) return true;

  return false;
};
